import React, { useMemo, useState } from 'react';
import * as S from './styles';
import { faEraser, faPlay } from '@fortawesome/free-solid-svg-icons';
import { DateInput } from '../../../components/interface/DateInput';
import Table from '../../../components/interface/Tabela';
import ActionButtons from '../../../components/interface/ActionButtons';
import { EntregadorRelatorio, RelatoriosEntregadoresViewProps, NotaEntregador } from './types';
import { DefaultTheme, useTheme } from 'styled-components';
import { api, API_ROUTERS } from '../../../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const RelatoriosEntregadoresContent: React.FC<RelatoriosEntregadoresViewProps> = ({
  relatorioEntregador,
  loading,
  error,
  formData,
  handleInputChange,
  handleSearch,
  handleClear
}) => {
  const theme = useTheme();

  const columns = [
    { key: 'codigo' as keyof EntregadorRelatorio, header: 'Código', width: '80px', filterable: true },
    { key: 'entregador' as keyof EntregadorRelatorio, header: 'Entregador', filterable: true },
    {
      key: 'data' as keyof EntregadorRelatorio,
      header: 'Data',
      filterable: true,
      render: (item: EntregadorRelatorio) => {
        const date = new Date(item.data);
        return date.toLocaleDateString('pt-BR');
      }
    },
    { key: 'entregas' as keyof EntregadorRelatorio, header: 'Número de Entregas', filterable: true },
  ];

  const notasColumns = [
    { key: 'chavenotafiscal' as keyof NotaEntregador, header: 'Chave da Nota', filterable: true },
    { key: 'status' as keyof NotaEntregador, header: 'Status', filterable: true },
    {
      key: 'data' as keyof NotaEntregador,
      header: 'Data/Hora',
      filterable: true,
      render: (item: NotaEntregador) => {
        const date = new Date(item.data);
        return date.toLocaleString('pt-BR');
      }
    },
    {
      key: 'imagem' as keyof NotaEntregador,
      header: 'Comprovante',
      render: (item: NotaEntregador) => item.imagem ? (
        <a href={item.imagem} target="_blank" rel="noopener noreferrer">
          Ver Comprovante
        </a>
      ) : 'Sem comprovante'
    },
    {
      key: 'codigoviagem' as keyof NotaEntregador,
      header: 'Código da Viagem',
      render: (item: NotaEntregador) => item.codigoviagem || '-'
    }
  ];

  // Função para buscar as notas do entregador
  const fetchNotasEntregador = async (entregador: EntregadorRelatorio) => {
    try {
      // Buscar as notas do entregador para a data selecionada
      const response = await api.get(`${API_ROUTERS.RELATORIO}?dataInicio=${entregador.data}&dataFim=${entregador.data}&usuario=${entregador.codigo}`);
      const notas = api.utils.trataResponseArray(response) as NotaEntregador[];
      console.log(notas);
      return notas;
    } catch (error) {
      console.error('Erro ao buscar notas do entregador:', error);
      return [];
    }
  };

  const chartData = useMemo(() => {
    const sortedData = [...relatorioEntregador].sort((a, b) => a.entregas - b.entregas);

    return sortedData.map(item => ({
      name: item.entregador.length > 20
        ? `${item.entregador.substring(0, 20)}...`
        : item.entregador,
      entregas: item.entregas,
      codigoEntregador: item.codigo
    }));
  }, [relatorioEntregador]);

  const actions = useMemo(() => [
    {
      icon: faPlay,
      onClick: handleSearch,
      title: 'Buscar',
      children: 'Buscar',
      disabled: loading,
      backgroundColor: (theme: DefaultTheme) => theme.primaryColor
    },
    {
      icon: faEraser,
      onClick: handleClear,
      title: 'Limpar',
      children: 'Limpar',
      backgroundColor: '#4dc9c9'
    }
  ], [handleSearch, loading, handleClear]);

  // Formatador personalizado para a legenda
  const customLegendFormatter = (value: string) => {
    return <S.LegendText>{value}</S.LegendText>;
  };

  return (
    <S.RelatoriosContainer>
      <S.RelatoriosContent>
        <S.FilterContainer>
          <S.FilterGroup>
            <DateInput
              id="data"
              setValue={(value) => handleInputChange('data', value)}
              initialValue={formData.data}
              hasLabel={true}
              label="Data"
              required={true}
              labelPosition="left"
            />
          </S.FilterGroup>
        </S.FilterContainer>

        <S.ButtonContainer>
          <ActionButtons buttons={actions} />
        </S.ButtonContainer>

        {error && (
          <S.ErrorContainer>
            {error}
          </S.ErrorContainer>
        )}

        {relatorioEntregador.length > 0 && (
          <S.ChartContainer>
            <S.ChartWrapper>
              <S.ChartTitle>Gráfico de Entregas por Entregador</S.ChartTitle>
              <ResponsiveContainer width="100%" height={450}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 80
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tickMargin={15}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => {
                      return [value, 'Entregas'];
                    }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        const fullName = relatorioEntregador.find(
                          item => item.codigo === payload[0].payload.codigoEntregador
                        )?.entregador;
                        return fullName || label;
                      }
                      return label;
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={50}
                    wrapperStyle={{
                      paddingTop: 30,
                      marginTop: 40,
                      bottom: 10
                    }}
                    formatter={customLegendFormatter}
                  />
                  <Bar
                    dataKey="entregas"
                    name="Número de Entregas"
                    fill="#8884d8"
                  />
                </BarChart>
              </ResponsiveContainer>
            </S.ChartWrapper>
          </S.ChartContainer>
        )}

        <S.TableContainer>
          {relatorioEntregador.length > 0 ? (
            <Table
              data={relatorioEntregador}
              columns={columns}
              exportable={true}
              expandable={{
                enabled: true,
                fetchChildData: fetchNotasEntregador,
                childColumns: notasColumns,
              }}
            />
          ) : (
            <S.NoDataContainer>
              {loading ? 'Carregando...' : 'Nenhum dado encontrado'}
            </S.NoDataContainer>
          )}
        </S.TableContainer>
      </S.RelatoriosContent>
    </S.RelatoriosContainer>
  );
};

export default RelatoriosEntregadoresContent;
